import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Games.css";

import { getAllGameModes } from "../../api/gameModeApi";
import { getLobbyByGameMode } from "../../api/lobbyApi";
import GameHeader from "../../components/Header/GameHeader";

export default function Games() {
  const navigate = useNavigate();

  /* ================= STATE ================= */
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedLobby, setSelectedLobby] = useState(null);

  const [lobbies, setLobbies] = useState([]);
  const [loading, setLoading] = useState(false);

  /* ================= FETCH GAME MODES ================= */
  const fetchLobbies = async () => {
    try {
      setLoading(true);
      const res = await getAllGameModes();
      setLobbies(res.data || []);
    } catch (err) {
      console.error("Failed to fetch lobbies:", err);
      setLobbies([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLobbies();
  }, []);

  /* ================= FILTER ================= */
  const filteredLobbies = lobbies.filter((row) => {
    const matchesStatus =
      statusFilter === "all"
        ? true
        : row.lobbyType?.toLowerCase() === statusFilter;

    const matchesSearch = row.lobbyType
      ?.toLowerCase()
      .includes(searchText.toLowerCase());

    return matchesStatus && matchesSearch;
  });

  return (
    <div className="games-container">
      {/* ================= HEADER ================= */}
      <GameHeader
        searchTerm={searchText}
        setSearchTerm={setSearchText}
      />

      {/* ================= MAIN LIST ================= */}
      {!selectedLobby && (
        <MainLobbyList
          lobbyRows={filteredLobbies}
          loading={loading}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          onView={(row) => setSelectedLobby(row)}
          onChest={() => navigate("/Layout/Chest")}
          onSpin={() => navigate("/Layout/Spin")}
        />
      )}

      {/* ================= DETAILS ================= */}
      {selectedLobby && (
        <LobbyDetails
          lobby={selectedLobby}
          onBack={() => setSelectedLobby(null)}
        />
      )}
    </div>
  );
}

/* =====================================================
   MAIN LOBBY LIST
===================================================== */
function MainLobbyList({
  lobbyRows,
  loading,
  statusFilter,
  setStatusFilter,
  onView,
  onChest,
  onSpin,
}) {
  return (
    <>
      <div className="games-section-alignment">
        <h5>Current Active Lobbies</h5>

        <div className="games-header-right">
          <button className="chip-btn" onClick={onChest}>
            Chest
          </button>
          <button className="chip-btn" onClick={onSpin}>
            Spin Wheel
          </button>

          <select
            className="games-filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All</option>
            <option value="timely">Timely</option>
            <option value="infinite">Infinite</option>
          </select>
        </div>
      </div>

      <div className="games-main">
        <table className="games-table">
          <thead>
            <tr>
              <th>Lobby Type</th>
              <th>Time</th>
              <th>Max Users</th>
              <th>Total Lobby</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {loading && (
              <tr>
                <td colSpan="5" style={{ textAlign: "center" }}>
                  Loading...
                </td>
              </tr>
            )}

            {!loading &&
              lobbyRows.map((row) => (
                <tr key={row._id}>
                  <td>{row.lobbyType}</td>
                  <td>{row.time || "NA"}</td>
                  <td>{row.maxUsers || "-"}</td>
                  <td>{row.totalLobby || "-"}</td>
                  <td>
                    <button
                      className="games-action-btn"
                      onClick={() => onView(row)}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}

            {!loading && lobbyRows.length === 0 && (
              <tr>
                <td colSpan="5" style={{ textAlign: "center" }}>
                  No lobbies found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

/* =====================================================
   LOBBY DETAILS
===================================================== */
function LobbyDetails({ lobby, onBack }) {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchLobbyDetails = async () => {
      try {
        setLoading(true);
        const res = await getLobbyByGameMode(lobby._id);
        setGames(res.data || []);
      } catch (err) {
        console.error("Failed to fetch lobby games:", err);
        setGames([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLobbyDetails();
  }, [lobby]);

  return (
    <div className="games-main">
      <div className="games-top-row">
        <h3>Lobby: {lobby.lobbyType}</h3>
        <button className="games-secondary-btn" onClick={onBack}>
          Back
        </button>
      </div>

      <table className="games-table">
        <thead>
          <tr>
            <th>User</th>
            <th>Score</th>
            <th>Kills</th>
            <th>Date</th>
          </tr>
        </thead>

        <tbody>
          {loading && (
            <tr>
              <td colSpan="4" style={{ textAlign: "center" }}>
                Loading...
              </td>
            </tr>
          )}

          {!loading &&
            games.map((game) => (
              <tr key={game._id}>
                <td>{game.username}</td>
                <td>{game.score}</td>
                <td>{game.kills}</td>
                <td>{game.date}</td>
              </tr>
            ))}

          {!loading && games.length === 0 && (
            <tr>
              <td colSpan="4" style={{ textAlign: "center" }}>
                No game records found
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
